import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BadgeCheck, TriangleAlert } from "lucide-react";

// Lote 158 — "OABs do escritório": quem tem qual OAB, quando foi a última busca e quanto chegou nos últimos 30 dias.
// Todos do escritório veem. Incluir uma OAB continua com o administrador da plataforma (regra de segurança do Lote 87):
// por isso o formulário só aparece para ele.
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

function ha(iso) {
  if (!iso) return "ainda não buscou";
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (Number.isNaN(min)) return "—";
  if (min < 60) return `há ${Math.max(min, 1)} min`;
  if (min < 60 * 24) return `há ${Math.round(min / 60)} h`;
  return `há ${Math.round(min / 1440)} d`;
}

export default function OabsEscritorio() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);      // {user_id, numero, uf}
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setD((await http.get("/escritorio/oabs")).data); } catch { setD({ erro: true }); } };
  useEffect(() => { load(); }, []);
  if (!d) return null;
  if (d.erro) return <p className="mt-6 text-xs text-stone-500" data-testid="oabs-erro">Não consegui carregar as OABs agora.</p>;
  if (!Array.isArray(d.pessoas)) return null;

  const incluir = async (ev) => {
    ev.preventDefault(); setBusy(true);
    try {
      const r = (await http.post(`/admin/users/${form.user_id}/oab`, { numero: form.numero, uf: form.uf })).data;
      toast.success(r.oab_em_outra_conta ? "OAB incluída — atenção: ela também está em outra conta" : "OAB incluída");
      setForm(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível incluir"); } finally { setBusy(false); }
  };

  return (
    <section aria-label="OABs do escritório" className="mt-6 rounded-xl border border-stone-200 bg-white p-5" data-testid="oabs-escritorio">
      <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2 mb-1"><BadgeCheck className="w-4 h-4" /> OABs do escritório</h2>
      <p className="text-xs text-stone-500 mb-3">As publicações chegam pela OAB de cada advogado. Quando um ato cita mais de um de vocês, Processos e Agenda mostram uma vez só, com o aviso "também na conta de…".{!d.pode_incluir && " Para incluir uma OAB, peça ao administrador da plataforma."}</p>
      <ul className="divide-y divide-stone-100">
        {d.pessoas.map((p) => (
          <li key={p.user_id} data-testid={`oabs-pessoa-${p.user_id}`} className="py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-stone-900">{p.nome}</span>
              {p.ex_membro && <span className="text-[11px] text-stone-500">(saiu)</span>}
              {d.pode_incluir && !p.ex_membro && p.papel !== "apoio" && (
                <button type="button" data-testid={`oab-incluir-${p.user_id}`} onClick={() => setForm({ user_id: p.user_id, numero: "", uf: "SP" })} className="ml-auto text-xs font-semibold text-stone-700 underline underline-offset-2">Incluir OAB</button>
              )}
            </div>
            {p.oabs.length === 0 ? (
              <p className="text-xs text-stone-500 mt-1" data-testid={`oabs-vazio-${p.user_id}`}>{p.papel === "apoio" ? "Apoio: não precisa de OAB." : "Ainda sem OAB cadastrada."}</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {p.oabs.map((o) => (
                  <li key={o.oab_id} className="text-sm text-stone-700 flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`oab-${o.oab_id}`}>
                    <b className="tabular-nums">OAB {o.numero}/{o.uf}</b>
                    <span className="text-xs text-stone-500">última busca {ha(o.ultima_busca)} · {o.ultimos_30_dias} {o.ultimos_30_dias === 1 ? "publicação" : "publicações"} em 30 dias{o.busca_por_nome ? " · busca por nome ligada" : ""}</span>
                    {o.bloqueio_regiao && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><TriangleAlert className="w-3 h-3" /> busca bloqueada por região</span>}
                  </li>
                ))}
              </ul>
            )}
            {form?.user_id === p.user_id && (
              <form onSubmit={incluir} className="mt-2 flex flex-wrap items-end gap-2" data-testid="oab-form">
                <label className="text-xs text-stone-500">Número
                  <Input data-testid="oab-form-numero" inputMode="numeric" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value.replace(/\D/g, "").slice(0, 7) })} className="mt-1 w-32" placeholder="252280" />
                </label>
                <label className="text-xs text-stone-500">UF
                  <select data-testid="oab-form-uf" value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value })} className="mt-1 block h-10 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                    {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
                <Button type="submit" size="sm" disabled={busy || form.numero.length < 2} data-testid="oab-form-salvar" className="bg-stone-900 text-white hover:bg-stone-800">Incluir</Button>
                <button type="button" onClick={() => setForm(null)} className="text-xs text-stone-500 underline pb-2">cancelar</button>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
